import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { APP_LOCALES, DEFAULT_LOCALE, resolveSupportedLocale } from './locales';

// All translation files are bundled at build time so the app works fully
// offline. New languages: add a JSON file under `src/i18n/locales/` AND
// import + register it here.
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import it from './locales/it.json';
import ptBR from './locales/pt-BR.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import sv from './locales/sv.json';
import no from './locales/no.json';
import da from './locales/da.json';
import fi from './locales/fi.json';
import ru from './locales/ru.json';
import tr from './locales/tr.json';
import ar from './locales/ar.json';
import zhCN from './locales/zh-CN.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import hi from './locales/hi.json';
import id from './locales/id.json';

const STORAGE_KEY = 'straiv_app_language';

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  it: { translation: it },
  'pt-BR': { translation: ptBR },
  nl: { translation: nl },
  pl: { translation: pl },
  sv: { translation: sv },
  no: { translation: no },
  da: { translation: da },
  fi: { translation: fi },
  ru: { translation: ru },
  tr: { translation: tr },
  ar: { translation: ar },
  'zh-CN': { translation: zhCN },
  ja: { translation: ja },
  ko: { translation: ko },
  hi: { translation: hi },
  id: { translation: id },
};

/**
 * Read the user's preferred locale BEFORE i18next initialises:
 *   1. Explicit override saved by the Settings picker (localStorage).
 *   2. `navigator.language` (inside Capacitor's WKWebView this reflects the
 *      iOS device locale exactly, so we don't need a separate native plugin).
 *   3. `en` as last resort.
 *
 * We resolve through `resolveSupportedLocale` so unsupported codes fall back
 * to a sensible match (e.g. "pt-PT" -> "pt-BR" because that's what we ship).
 */
function detectInitialLocale(): string {
  if (typeof window !== 'undefined') {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) return resolveSupportedLocale(saved);
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return resolveSupportedLocale(navigator.language);
  }
  return DEFAULT_LOCALE;
}

/**
 * Persist the user's choice and switch the active language.
 * Other components react via `useTranslation()` which subscribes to i18next.
 */
export async function setAppLanguage(code: string): Promise<void> {
  const target = resolveSupportedLocale(code);
  await i18n.changeLanguage(target);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, target);
  }
  // Tag the <html> element so any RTL-aware CSS can react. We currently
  // keep layout LTR for Arabic (per product decision), but text direction
  // on inputs etc. still benefits from the correct dir attribute.
  if (typeof document !== 'undefined') {
    const meta = APP_LOCALES.find((l) => l.code === target);
    document.documentElement.lang = target;
    document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr';
  }
}

let initPromise: Promise<typeof i18n> | null = null;

/**
 * Initialise i18next once. Returns the same promise on subsequent calls so
 * `main.tsx` can await it before mounting React without racing.
 */
export function initI18n(): Promise<typeof i18n> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const initial = detectInitialLocale();

    await i18n
      .use(LanguageDetector) // Detector is registered but we override its result with our async detection above.
      .use(initReactI18next)
      .init({
        resources,
        lng: initial,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: APP_LOCALES.map((l) => l.code),
        interpolation: {
          escapeValue: false, // React already escapes
        },
        returnNull: false,
        react: {
          useSuspense: false,
        },
      });

    if (typeof document !== 'undefined') {
      const meta = APP_LOCALES.find((l) => l.code === initial);
      document.documentElement.lang = initial;
      document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr';
    }

    return i18n;
  })();
  return initPromise;
}

export { i18n, STORAGE_KEY as APP_LANGUAGE_STORAGE_KEY };
export { APP_LOCALES, DEFAULT_LOCALE, SUPPORTED_LOCALE_CODES } from './locales';
export type { AppLocale } from './locales';
